/**
 * RESPONSIBILITY: the word that stands for a row in a URL.
 *
 * A row is addressed by identity and never by position — `#ministry/2` means a different
 * ministry the moment somebody reorders the list. But an id is not the only stable
 * answer, and it is the ugly one: `#ministry/mn0001prayer` is a link nobody wants to
 * share, and a ministry added through the editor would be worse, because a fresh row id
 * is twelve random characters.
 *
 * So the URL prefers a word and the id remains the fallback. Old links keep working —
 * that is the whole point of not throwing the id away.
 *
 * NO MIGRATION was needed for this: a row with no slug falls back to its title, and
 * "Prayer" was always going to slugify to `prayer`. Rewriting the seeded list to add a
 * field would have overwritten whatever an editor had already changed on the deployment.
 */
import { idOf, rowById } from '@framework/core';
import { slugify } from '@app/sections/commons/slug';

export { slugify };

/** What a row needs for any of this to mean anything. */
interface Addressable { title?: string; slug?: string; url?: string }

/**
 * What this row's link says. A resolved `url` wins where the row has one — it is the only
 * form guaranteed unique — then an explicit slug, then the title, then the id, which is
 * what an untitled row still has.
 */
export const keyOf = (row: Addressable): string =>
  row.url?.trim() || row.slug?.trim() || slugify(row.title ?? '') || idOf(row as object);

/**
 * The row a URL points at. Matches what the links say first, then falls back to the row
 * id so every link shared before this existed still resolves.
 *
 * Two rows sharing a title share a slug, and the first wins. That is what the slug field
 * is for; it is not worth refusing to render a page over.
 */
export function bySlugOrId<T extends Addressable & object>(
  rows: readonly T[] | undefined, key: string | undefined,
): T | undefined {
  if (!rows || !key) return undefined;
  // Every form this row has ever been addressed by, newest first. A link shared before
  // `url` existed pointed at the title, and it must not stop working because it did.
  return rows.find((r) => keyOf(r) === key)
    ?? rows.find((r) => r.slug?.trim() === key)
    ?? rows.find((r) => slugify(r.title ?? '') === key)
    ?? rowById(rows, key);
}
